export const SERVICE_HISTORY_UPLOAD_DB_NAME =
  "autoeinfachlos-service-history-uploads";
export const SERVICE_HISTORY_UPLOAD_DB_VERSION = 1;
export const SERVICE_HISTORY_UPLOAD_STORE_NAME =
  "service-history-upload-records";

const OWNER_INDEX = "ownerEmail";
const DEFAULT_OPEN_TIMEOUT_MS = 8_000;
const DEFAULT_TRANSACTION_TIMEOUT_MS = 15_000;
const TERMINAL_STATES = new Set(["confirmed", "deleted"]);

let databasePromise = null;
let openDatabase = null;
let openTimeoutMs = DEFAULT_OPEN_TIMEOUT_MS;
let transactionTimeoutMs = DEFAULT_TRANSACTION_TIMEOUT_MS;

function storageTimeoutError(operation) {
  const error = new Error(
    `IndexedDB hat beim ${operation} des Serviceheft-Uploads nicht rechtzeitig geantwortet.`,
  );
  error.name = "ServiceHistoryUploadStorageTimeoutError";
  error.code = "SERVICE_HISTORY_UPLOAD_STORAGE_TIMEOUT";
  return error;
}

function assertRecord(record) {
  if (!record || typeof record !== "object" || !String(record.key || "")) {
    throw new TypeError(
      "Ein Serviceheft-Upload benötigt einen gültigen Record.",
    );
  }
  if (!String(record.uploadId || "")) {
    throw new TypeError(
      "Ein Serviceheft-Upload benötigt eine gültige Upload-ID.",
    );
  }
}

function openServiceHistoryUploadDatabase() {
  if (!globalThis?.indexedDB) {
    const error = new Error("IndexedDB ist in dieser Umgebung nicht verfügbar.");
    error.name = "NotSupportedError";
    throw error;
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const request = globalThis.indexedDB.open(
      SERVICE_HISTORY_UPLOAD_DB_NAME,
      SERVICE_HISTORY_UPLOAD_DB_VERSION,
    );
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(storageTimeoutError("Öffnen"));
    }, openTimeoutMs);

    const fail = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      reject(
        error ||
          new Error("IndexedDB für Serviceheft-Uploads konnte nicht geöffnet werden."),
      );
    };

    request.onupgradeneeded = () => {
      const database = request.result;
      const store = database.objectStoreNames.contains(
        SERVICE_HISTORY_UPLOAD_STORE_NAME,
      )
        ? request.transaction.objectStore(SERVICE_HISTORY_UPLOAD_STORE_NAME)
        : database.createObjectStore(SERVICE_HISTORY_UPLOAD_STORE_NAME, {
            keyPath: "key",
          });
      if (!store.indexNames.contains(OWNER_INDEX)) {
        store.createIndex(OWNER_INDEX, "ownerEmail", { unique: false });
      }
    };
    request.onerror = () => fail(request.error);
    request.onblocked = () =>
      fail(new Error("IndexedDB für Serviceheft-Uploads ist blockiert."));
    request.onsuccess = () => {
      const database = request.result;
      if (settled) {
        database.close();
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      openDatabase = database;
      database.onversionchange = () => {
        database.close();
        if (openDatabase === database) openDatabase = null;
        databasePromise = null;
      };
      resolve(database);
    };
  });
}

function getDatabase() {
  if (!databasePromise) {
    const nextPromise = openServiceHistoryUploadDatabase();
    databasePromise = nextPromise;
    void Promise.resolve(nextPromise).catch(() => {
      if (databasePromise === nextPromise) databasePromise = null;
    });
  }
  return databasePromise;
}

async function runTransaction(mode, work) {
  const database = await getDatabase();

  return new Promise((resolve, reject) => {
    let result;
    let settled = false;
    const transaction = database.transaction(
      SERVICE_HISTORY_UPLOAD_STORE_NAME,
      mode,
    );
    const store = transaction.objectStore(SERVICE_HISTORY_UPLOAD_STORE_NAME);
    const timeoutId = setTimeout(() => {
      fail(storageTimeoutError("Speichern"));
      database.close();
      if (openDatabase === database) openDatabase = null;
      databasePromise = null;
    }, transactionTimeoutMs);

    const fail = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      try {
        transaction.abort();
      } catch {}
      reject(
        error ||
          new Error("IndexedDB-Transaktion für Serviceheft-Uploads fehlgeschlagen."),
      );
    };

    transaction.oncomplete = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve(result);
    };
    transaction.onerror = () => fail(transaction.error);
    transaction.onabort = () => fail(transaction.error);

    try {
      work(
        store,
        (value) => {
          result = value;
        },
        fail,
      );
    } catch (error) {
      fail(error);
    }
  });
}

export async function putServiceHistoryUploadRecord(record) {
  assertRecord(record);
  return runTransaction("readwrite", (store, setResult, fail) => {
    const getRequest = store.get(record.key);
    getRequest.onerror = () => fail(getRequest.error);
    getRequest.onsuccess = () => {
      const current = getRequest.result;
      if (
        current &&
        current.uploadId !== record.uploadId
      ) {
        setResult(false);
        return;
      }
      if (
        current &&
        TERMINAL_STATES.has(current.state) &&
        !TERMINAL_STATES.has(record.state)
      ) {
        setResult(false);
        return;
      }
      const putRequest = store.put(record);
      putRequest.onerror = () => fail(putRequest.error);
      putRequest.onsuccess = () => setResult(true);
    };
  });
}

export async function updateServiceHistoryUploadRecord(
  key,
  uploadId,
  patch,
) {
  return runTransaction("readwrite", (store, setResult, fail) => {
    const getRequest = store.get(key);
    getRequest.onerror = () => fail(getRequest.error);
    getRequest.onsuccess = () => {
      const current = getRequest.result;
      if (
        !current ||
        current.uploadId !== uploadId ||
        TERMINAL_STATES.has(current.state)
      ) {
        setResult(false);
        return;
      }
      const putRequest = store.put({
        ...current,
        ...patch,
        key,
        uploadId,
      });
      putRequest.onerror = () => fail(putRequest.error);
      putRequest.onsuccess = () => setResult(true);
    };
  });
}

export async function getServiceHistoryUploadRecordsByOwner(ownerEmail) {
  if (!ownerEmail) return [];
  return runTransaction("readonly", (store, setResult, fail) => {
    const request = store
      .index(OWNER_INDEX)
      .getAll(String(ownerEmail).trim().toLowerCase());
    request.onerror = () => fail(request.error);
    request.onsuccess = () => setResult(request.result || []);
  });
}

export async function deleteServiceHistoryUploadRecord(key, uploadId) {
  return runTransaction("readwrite", (store, setResult, fail) => {
    const getRequest = store.get(key);
    getRequest.onerror = () => fail(getRequest.error);
    getRequest.onsuccess = () => {
      const current = getRequest.result;
      if (!current || current.uploadId !== uploadId) {
        setResult(false);
        return;
      }
      const deleteRequest = store.delete(key);
      deleteRequest.onerror = () => fail(deleteRequest.error);
      deleteRequest.onsuccess = () => setResult(true);
    };
  });
}

export async function clearServiceHistoryUploadRecordsForTests() {
  return runTransaction("readwrite", (store, setResult, fail) => {
    const request = store.clear();
    request.onerror = () => fail(request.error);
    request.onsuccess = () => setResult(true);
  });
}

export function closeServiceHistoryUploadStoreForTests() {
  openDatabase?.close();
  openDatabase = null;
  databasePromise = null;
  openTimeoutMs = DEFAULT_OPEN_TIMEOUT_MS;
  transactionTimeoutMs = DEFAULT_TRANSACTION_TIMEOUT_MS;
}

export function setServiceHistoryUploadStoreTimeoutsForTests({
  databaseOpenTimeoutMs = DEFAULT_OPEN_TIMEOUT_MS,
  transactionDeadlineMs = DEFAULT_TRANSACTION_TIMEOUT_MS,
} = {}) {
  openTimeoutMs = Math.max(1, Number(databaseOpenTimeoutMs) || 1);
  transactionTimeoutMs = Math.max(1, Number(transactionDeadlineMs) || 1);
}
